import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Input,
  message,
  Popconfirm,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import moment from 'moment';
import { groupBy, values } from 'lodash';
import { stringify } from 'csv-stringify/dist/esm';
import FileSaver from 'file-saver';
import React, { useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import {
  ServiceOffersQuery,
  ServicesQuery,
  useAcceptServiceOfferBidMutation,
  useDeleteServiceOffersMutation,
  useIgnoreServiceOfferBidMutation,
  useOfferServiceMutation,
  useRejectServiceOfferBidMutation,
  useServiceOffersQuery,
  useServicesQuery,
} from '../../graphql/datamodel.gen';
import { DrivingCompaniesPostulations } from './components/drivingCompaniesPostulations';
import ServiceDetails from '../services/components/ServiceDetails/ServiceDetails';

type ServiceRow = ServicesQuery['services'][0];
type ServiceOfferRow = ServiceOffersQuery['serviceOffers'][0];

const onMutationError = () => {
  message.error('Ha ocurrido un error al realizar la operacion');
};

export const ServiceOffers = () => {
  const [serviceToShow, setServiceToShow] = useState<string | undefined>();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [offeringService, setOfferingService] = useState<string[]>([]);
  const [deletingServiceOffer, setDeletingServiceOffer] = useState<string[]>(
    [],
  );
  const searchInput = useRef<Input | null>(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState();
  const [pickerOpened, setPickerOpened] = useState(false);
  const [[departureStart, departureEnd], setDepartureStartEnd] = useState([
    moment(),
    moment(),
  ]);
  const servicesQuery = useServicesQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      departureDateMin: departureStart.format('YYYY-MM-DD'),
      departureDateMax: departureEnd.format('YYYY-MM-DD'),
      includeOffers: true,
      includeDrivingCompany: true,
    },
  });

  const serviceOffersQuery = useServiceOffersQuery({
    fetchPolicy: 'cache-and-network',
  });

  const offerServiceMutation = useOfferServiceMutation();
  const deleteServiceOffersMutation = useDeleteServiceOffersMutation();

  const acceptServiceOfferBidMutation = useAcceptServiceOfferBidMutation();
  const rejectServiceOfferBidMutation = useRejectServiceOfferBidMutation();
  const ignoreServiceOfferBidMutation = useIgnoreServiceOfferBidMutation();

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined translate={undefined} />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{ color: filtered ? '#1890ff' : undefined }}
        translate={undefined}
      />
    ),
    onFilter: (value, record) => {
      const searchValue = dataIndex.split('.').reduce(function (a, b) {
        return a[b];
      }, record);

      return searchValue
        ? searchValue.toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput?.current?.select(), 100);
      }
    },
    ellipsis: {
      showTitle: false,
    },
    render: (text) => {
      const component =
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text ? text.toString() : ''}
          />
        ) : (
          text
        );

      return (
        <Tooltip placement="topLeft" title={text}>
          {component}
        </Tooltip>
      );
    },
  });

  const offerService = (serviceIds: string[]) => {
    setOfferingService(serviceIds);
    offerServiceMutation[0]({
      variables: {
        serviceIds,
      },
      onCompleted: () => {
        setOfferingService([]);
        if (selectedRowKeys?.length) {
          setSelectedRowKeys([]);
        }

        message.success(
          serviceIds?.length > 0
            ? 'Servicios ofertados correctamente'
            : 'Servicio ofertado correctamente',
        );
        servicesQuery.refetch();
        serviceOffersQuery.refetch();
      },
      onError: () => {
        message.error('Ha ocurrido un error al realizar la operación');
        setOfferingService([]);
      },
    });
  };

  const deleteServiceOffer = (offersIds: string[]) => {
    setDeletingServiceOffer(offersIds);
    deleteServiceOffersMutation[0]({
      variables: {
        ids: offersIds,
      },
      onCompleted: () => {
        setDeletingServiceOffer([]);
        if (selectedRowKeys?.length) {
          setSelectedRowKeys([]);
        }
        message.success(
          offersIds.length > 0
            ? 'Ofertas de servicio eliminadas correctamente'
            : 'Oferta de servicio eliminada correctamente',
        );
        servicesQuery.refetch();
        serviceOffersQuery.refetch();
      },
      onError: () => {
        message.error('Ha ocurrido un error al realizar la operación');
        setDeletingServiceOffer([]);
      },
    });
  };

  const servicesToOffer = servicesQuery.data?.services
    ?.filter((service) => selectedRowKeys.find((k) => k === service.id))
    .filter((s) => !s.offers?.length);

  const servicesToUnoffer = servicesQuery.data?.services
    ?.filter((service) => selectedRowKeys.find((k) => k === service.id))
    .filter((s) => s.offers?.length > 0);

  const onAcceptBid = (serviceOfferId: string, bidId: string) => {
    acceptServiceOfferBidMutation[0]({
      variables: {
        serviceOfferId,
        bidId,
      },
      onCompleted() {
        message.success('Postulación aceptada correctamente');
      },
      onError: onMutationError,
    });
  };

  const onRejectBid = (serviceOfferId: string, bidId: string) => {
    rejectServiceOfferBidMutation[0]({
      variables: {
        serviceOfferId,
        bidId,
      },
      onCompleted() {
        message.success('Postulación rechazada correctamente');
      },
      onError: onMutationError,
    });
  };

  const onIgnoreBid = (serviceOfferId: string, bidId: string) => {
    ignoreServiceOfferBidMutation[0]({
      variables: {
        serviceOfferId,
        bidId,
      },
      onCompleted() {
        message.success('Postulación ignorada correctamente');
      },
      onError: onMutationError,
    });
  };

  const onExportCSV = () => {
    const csv = [
      [
        'CÓDIGO',
        'FECHA DE SALIDA',
        'FECHA DE LLEGADA',
        'ITINERARIO',
        'TIPO DE VEHÍCULO',
        'PAX',
      ],
      ...serviceOffersQuery.data?.serviceOffers?.map((so) => {
        const { service } = so;

        return [
          service.volcanoServiceId,
          `${service.departure.date} ${service.departure.time}`,
          `${service.arrival.date} ${service.arrival.time}`,
          service.description,
          service.requestedVehicleDescription,
          service.numOfPassengers,
        ];
      }),
    ];

    stringify(
      csv,
      {
        quoted: true,
      },
      (err, data) => {
        const file = new File([data], 'ofertas-de-servicio.csv', {
          type: 'text/csv;charset=utf-8',
        });

        FileSaver.saveAs(file);
      },
    );
  };

  const onViewService = (serviceId: string) => {
    setServiceToShow(serviceId);
  };

  const onCloseServiceDetailsDrawer = () => {
    setServiceToShow(undefined);
  };

  return (
    <>
      <Tabs type="card">
        <Tabs.TabPane
          tab="Todos los servicios"
          key={'services'}
          tabKey="services"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <DatePicker.RangePicker
                format={'DD/MM/YYYY'}
                onChange={([departureStart, departureEnd]) => {
                  setDepartureStartEnd([departureStart, departureEnd]);
                }}
                // size={device.type === 'phone' ? 'large' : undefined}
                renderExtraFooter={() =>
                  'Por favor, selecciona un rango de fechas'
                }
                defaultValue={[departureStart, departureEnd]}
                defaultPickerValue={[departureStart, departureEnd]}
                // defaultValue={[moment(defaultDates?.[0]), moment(defaultDates?.[1])]}
                value={
                  !pickerOpened ? [departureStart, departureEnd] : undefined
                }
                ranges={{
                  Hoy: [moment(), moment()],
                  'Este mes': [
                    moment().startOf('month'),
                    moment().endOf('month'),
                  ],
                }}
                onOpenChange={(open) => {
                  setPickerOpened(open);
                }}
                allowClear={false}
              />
              <Button
                onClick={() => {
                  setDepartureStartEnd([moment(), moment()]);
                }}
              >
                Hoy
              </Button>
              <Button
                onClick={() => {
                  const tomorrow = moment().add(1, 'day');

                  setDepartureStartEnd([tomorrow, tomorrow]);
                }}
              >
                Mañana
              </Button>
              <Button
                onClick={() => {
                  const yesterday = moment().subtract(1, 'day');

                  setDepartureStartEnd([yesterday, yesterday]);
                }}
              >
                Ayer
              </Button>
              <Space>
                <Button
                  disabled={!servicesToOffer?.length}
                  onClick={() => {
                    offerService(servicesToOffer.map((s) => s.id));
                  }}
                >
                  {servicesToOffer?.length
                    ? `Ofertar ${servicesToOffer?.length} servicio(s)`
                    : 'Ofertar'}
                </Button>
                <Button
                  disabled={!servicesToUnoffer?.length}
                  onClick={() => {
                    deleteServiceOffer(
                      servicesToUnoffer.map((s) => s.offers[0].id),
                    );
                  }}
                >
                  {servicesToUnoffer?.length
                    ? `Eliminar ${servicesToUnoffer?.length} ofertas de servicio`
                    : 'Eliminar oferta'}
                </Button>
              </Space>
            </Space>
            <Table
              dataSource={servicesQuery.data?.services.filter((s) => {
                const isConfirmed = s.status === 'confirmed';
                const isAssignedToDrivingCompany = !!s.drivingCompany?.id;

                return isConfirmed && !isAssignedToDrivingCompany;
              })}
              loading={servicesQuery.loading}
              pagination={{
                pageSize: 10,
              }}
              rowSelection={{
                type: 'checkbox',
                onChange: (selectedRowKeys) => {
                  setSelectedRowKeys(selectedRowKeys);
                },
                selectedRowKeys,
                selections: [Table.SELECTION_ALL, Table.SELECTION_NONE],
              }}
              rowKey={(record: ServiceRow) => {
                return record.id;
              }}
              size="small"
              columns={[
                {
                  title: 'ID',
                  dataIndex: 'volcanoServiceId',
                  width: 100,
                  ...getColumnSearchProps('volcanoServiceId'),
                },
                {
                  title: 'Cliente',
                  dataIndex: ['client', 'name'],
                  // width: 400,
                  ...getColumnSearchProps('client.name'),
                  ellipsis: {
                    showTitle: false,
                  },
                  render: (value) => (
                    <Tooltip placement="topLeft" title={value}>
                      {value}
                    </Tooltip>
                  ),
                },
                {
                  title: 'Salida',
                  dataIndex: 'departure',
                  key: 'departure',
                  width: 150,
                  sortDirections: ['descend', 'ascend'],
                  defaultSortOrder: 'descend' as
                    | 'descend'
                    | 'ascend'
                    | undefined,
                  render: (_: any, record: any) => {
                    return (
                      <div>
                        {record.departure.date} <b>{record.departure.time}</b>
                      </div>
                    );
                  },
                  sorter: (a: any, b: any) => {
                    if (
                      a.departure &&
                      a.departure.time &&
                      a.departure.date &&
                      b.departure &&
                      b.departure.time &&
                      b.departure.date
                    ) {
                      const aDateTime = new Date(a.departure.date);
                      aDateTime.setHours(
                        Number((a.departure.time as string).split(':')[0]),
                        Number((a.departure.time as string).split(':')[1]),
                      );

                      const bDateTime = new Date(b.departure.date);
                      bDateTime.setHours(
                        Number((b.departure.time as string).split(':')[0]),
                        Number((b.departure.time as string).split(':')[1]),
                      );

                      return bDateTime.getTime() - aDateTime.getTime();
                    }

                    return 0;
                  },
                },
                {
                  title: 'Llegada',
                  dataIndex: 'arrival',
                  key: 'arrival',
                  width: 150,
                  render: (_: any, record: any) => {
                    const arrivalDate = moment(record.arrival.date);
                    const departureDate = moment(record.departure.date);
                    const durationAsDays = moment
                      .duration(arrivalDate.diff(departureDate))
                      .asDays();

                    if (durationAsDays >= 1) {
                      // Append +1 to the time
                      return (
                        <div>
                          <b>{record.arrival.time}</b>{' '}
                          <sup>+{durationAsDays}</sup>
                        </div>
                      );
                    } else {
                      // Just show the time
                      return (
                        <div>
                          <b>{record.arrival.time}</b>
                        </div>
                      );
                    }
                  },
                },
                {
                  title: '# PAX',
                  dataIndex: 'numOfPassengers',
                  width: 75,
                },
                {
                  width: 125,
                  title: 'Tipo de vehículo',
                  dataIndex: ['requestedVehicleDescription'],
                },
                {
                  title: 'Descripción',
                  dataIndex: 'description',
                  key: 'description',
                  ellipsis: {
                    showTitle: false,
                  },
                  render: (value) => (
                    <Tooltip placement="topLeft" title={value}>
                      {value}
                    </Tooltip>
                  ),
                },
                // {
                //   title: 'Estado',
                //   dataIndex: 'status',
                //   key: 'status',
                //   width: 150,
                //   filters: [
                //     {
                //       text: 'Asignado',
                //       value: 'assigned',
                //     },
                //     {
                //       text: 'Confirmado',
                //       value: 'confirmed',
                //     },
                //     {
                //       text: 'Cancelado',
                //       value: 'canceled',
                //     },
                //     {
                //       text: 'Finalizado',
                //       value: 'closed',
                //     },
                //     {
                //       text: 'Vehículo en origen',
                //       value: 'vehicle_on_departure_spot',
                //     },
                //     {
                //       text: 'En marcha',
                //       value: 'ongoing',
                //     },
                //   ],
                //   // filteredValue: (this.props.filteredInfo as any).status || null,
                //   onFilter: (filter: string, record: any) => {
                //     return record.status === filter;
                //   },
                //   defaultFilteredValue: ['confirmed'],
                //   sorter: (a: any, b: any) => a.status.length - b.status.length,
                //   render: (status?: ServiceStatus) => {
                //     const { color, text } = renderServiceStatus(status);
                //
                //     return (
                //       <Tag color={color} key={status || 'unknown'}>
                //         {text.toUpperCase()}
                //       </Tag>
                //     );
                //   },
                // },
                {
                  title: 'Ofertado',
                  sortDirections: ['descend', 'ascend'],
                  width: 100,
                  render: (service: ServiceRow) => {
                    if (service.offers?.length > 0) {
                      return <Tag color="success">Si</Tag>;
                    }

                    return <Tag>No</Tag>;
                  },
                  filters: [
                    {
                      text: 'Sí',
                      value: true,
                    },
                    {
                      text: 'No',
                      value: false,
                    },
                  ],
                  // filteredValue: (this.props.filteredInfo as any).status || null,
                  onFilter: (filter: boolean, record: ServiceRow) => {
                    const hasOffers = record.offers?.length > 0;
                    return hasOffers === filter;
                  },
                  sorter: (serviceA: ServiceRow, serviceB: ServiceRow) => {
                    return (
                      (serviceA.offers?.length || 0) -
                      (serviceB.offers?.length || 0)
                    );
                  },
                },
                {
                  title: 'Acciones',
                  width: 250,
                  render: (_, service: ServiceRow) => {
                    return (
                      <Space>
                        {service.offers?.length > 0 && (
                          <Popconfirm
                            title="¿Estás seguro de que deseas eliminar la oferta de este servicio?"
                            onConfirm={() => {
                              deleteServiceOffer([service.offers[0].id]);
                            }}
                          >
                            <Button size={'small'} danger type="ghost">
                              Eliminar oferta
                            </Button>
                          </Popconfirm>
                        )}

                        {!service.offers?.length && (
                          <Popconfirm
                            title="¿Estás seguro de que deseas ofertar este servicio?"
                            onConfirm={() => {
                              offerService([service.id]);
                            }}
                          >
                            <Button
                              size={'small'}
                              loading={
                                offeringService?.find(
                                  (s) => s === service.id,
                                ) && offerServiceMutation[1].loading
                              }
                            >
                              Ofertar
                            </Button>
                          </Popconfirm>
                        )}
                        <Button
                          size={'small'}
                          onClick={() =>
                            onViewService(service.volcanoServiceId)
                          }
                        >
                          Ver
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
            />
          </Space>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={`Servicios en oferta`}
          key={'offered_services'}
          tabKey="offered_services"
        >
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Space>
                <Button
                  disabled={!servicesToUnoffer?.length}
                  onClick={() => {
                    deleteServiceOffer(
                      servicesToUnoffer.map((s) => s.offers[0].id),
                    );
                  }}
                >
                  {servicesToUnoffer?.length
                    ? `Eliminar ${servicesToUnoffer?.length} ofertas de servicio`
                    : 'Eliminar oferta'}
                </Button>
              </Space>
              <Button
                icon={<DownloadOutlined translate={undefined} />}
                onClick={onExportCSV}
              >
                Exportar CSV
              </Button>
            </Space>
            <Table
              loading={serviceOffersQuery.loading}
              size="small"
              expandable={{
                expandRowByClick: true,
                expandedRowRender: (record) => {
                  return (
                    <>
                      <h4>Postulaciones</h4>
                      <Table
                        size={'small'}
                        bordered
                        dataSource={record.bids}
                        rowKey={(record) => record.id}
                        columns={[
                          {
                            title: 'Fecha de postulación',
                            dataIndex: 'createdAt',
                            defaultSortOrder: 'ascend',
                            sorter: (recordA, recordB) => {
                              return recordA.createdAt > recordB.createdAt
                                ? -1
                                : recordA.createdAt < recordB.createdAt
                                ? 1
                                : 0;
                            },
                            render: (createdAt: string) => {
                              return moment(createdAt).format(
                                'DD/MM/YY, HH:mm',
                              );
                            },
                          },
                          {
                            title: 'ID Empresa colaboradora',
                            render: (_: any, bidRecord) => {
                              return bidRecord.drivingCompany.volcanoId;
                            },
                          },
                          {
                            title: 'Nombre Empresa colaboradora',
                            render: (_: any, bidRecord) => {
                              return bidRecord.drivingCompany.name;
                            },
                          },
                          {
                            title: 'Estado',
                            render: (_: any, bidRecord) => {
                              if (bidRecord.status === 'PENDING') {
                                return <Tag>Pendiente</Tag>;
                              }
                              if (bidRecord.status === 'ACCEPTED') {
                                return <Tag color={'success'}>Aceptada</Tag>;
                              }
                              if (bidRecord.status === 'REJECTED') {
                                return <Tag color={'error'}>Rechazada</Tag>;
                              }
                            },
                          },
                          {
                            title: 'Acciones',
                            width: 200,
                            render: (_: any, bidRecord) => {
                              return (
                                <Space>
                                  <Button
                                    type={'primary'}
                                    size={'small'}
                                    disabled={bidRecord.status === 'ACCEPTED'}
                                    onClick={() => {
                                      onAcceptBid(record.id, bidRecord.id);
                                    }}
                                  >
                                    Aceptar
                                  </Button>
                                  <Button
                                    danger
                                    size={'small'}
                                    disabled={bidRecord.status === 'REJECTED'}
                                    onClick={() => {
                                      onRejectBid(record.id, bidRecord.id);
                                    }}
                                  >
                                    Rechazar
                                  </Button>
                                  <Button
                                    size={'small'}
                                    disabled={bidRecord.status === 'PENDING'}
                                    onClick={() => {
                                      onIgnoreBid(record.id, bidRecord.id);
                                    }}
                                  >
                                    Ignorar
                                  </Button>
                                </Space>
                              );
                            },
                          },
                        ]}
                      />
                    </>
                  );
                },
                rowExpandable: (record) => {
                  return record.bids?.length > 0;
                },
              }}
              rowKey={(record) => record.id}
              columns={[
                {
                  title: 'Fecha de oferta',
                  dataIndex: 'createdAt',
                  width: 125,
                  render: (date: string) => {
                    return moment(date).format('DD/MM/YYYY');
                  },
                },
                {
                  title: 'ID Servicio',
                  width: 100,
                  render: (_, offer: ServiceOfferRow) => {
                    return offer.service.volcanoServiceId;
                  },
                },
                {
                  title: 'Cliente',
                  dataIndex: ['service', 'client', 'name'],
                  ...getColumnSearchProps('service.client.name'),
                },
                {
                  title: 'Salida',
                  dataIndex: ['service', 'departure'],
                  width: 150,
                  sortDirections: ['descend', 'ascend'],
                  defaultSortOrder: 'descend' as
                    | 'descend'
                    | 'ascend'
                    | undefined,
                  render: (departure: any) => {
                    return (
                      <div>
                        {departure.date} <b>{departure.time}</b>
                      </div>
                    );
                  },
                  sorter: (departureA: any, departureB: any) => {
                    if (
                      departureA &&
                      departureA.time &&
                      departureA.date &&
                      departureB &&
                      departureB.time &&
                      departureB.date
                    ) {
                      const aDateTime = new Date(departureA.date);
                      aDateTime.setHours(
                        Number((departureA.time as string).split(':')[0]),
                        Number((departureA.time as string).split(':')[1]),
                      );

                      const bDateTime = new Date(departureB.date);
                      bDateTime.setHours(
                        Number((departureB.time as string).split(':')[0]),
                        Number((departureB.time as string).split(':')[1]),
                      );

                      return bDateTime.getTime() - aDateTime.getTime();
                    }

                    return 0;
                  },
                },
                {
                  title: 'Llegada',
                  dataIndex: ['service', 'arrival'],
                  width: 100,
                  render: (_: any, record: any) => {
                    const arrivalDate = moment(record.service.arrival.date);
                    const departureDate = moment(record.service.departure.date);
                    const durationAsDays = moment
                      .duration(arrivalDate.diff(departureDate))
                      .asDays();

                    if (durationAsDays >= 1) {
                      // Append +1 to the time
                      return (
                        <div>
                          <b>{record.service.arrival.time}</b>{' '}
                          <sup>+{durationAsDays}</sup>
                        </div>
                      );
                    } else {
                      // Just show the time
                      return (
                        <div>
                          <b>{record.service.arrival.time}</b>
                        </div>
                      );
                    }
                  },
                },
                {
                  title: '# PAX',
                  dataIndex: ['service', 'numOfPassengers'],
                  width: 75,
                },
                {
                  title: 'Descripción',
                  dataIndex: ['service', 'description'],
                  ellipsis: {
                    showTitle: false,
                  },
                  render: (value) => (
                    <Tooltip placement="topLeft" title={value}>
                      {value}
                    </Tooltip>
                  ),
                },
                {
                  title: 'Tipo de vehículo',
                  dataIndex: ['service', 'requestedVehicleDescription'],
                },
                {
                  title: '# Postulaciones',
                  key: 'bids',
                  width: 150,
                  render: (_, offer: ServiceOfferRow) => {
                    return offer.bids?.length || 0;
                  },
                  sorter: (
                    offerA: ServiceOfferRow,
                    offerB: ServiceOfferRow,
                  ) => {
                    return offerA.bids?.length || 0 - offerB.bids?.length || 0;
                  },
                },
                {
                  title: 'Acciones',
                  width: 250,
                  fixed: 'left',
                  render: (_, offer: ServiceOfferRow) => {
                    return (
                      <Space>
                        <Popconfirm
                          title="¿Estás seguro de que deseas eliminar la oferta de este servicio?"
                          onConfirm={() => {
                            deleteServiceOffer([offer.id]);
                          }}
                        >
                          <Button size={'small'} danger type="ghost">
                            Dejar de ofertar
                          </Button>
                        </Popconfirm>
                        <Button
                          size={'small'}
                          onClick={() =>
                            onViewService(offer.service.volcanoServiceId)
                          }
                        >
                          Ver
                        </Button>
                      </Space>
                    );
                  },
                },
              ]}
              dataSource={serviceOffersQuery.data?.serviceOffers}
            />
          </Space>
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={'Postulaciones por empresa'}
          key={'bids'}
          tabKey={'bids'}
        >
          <DrivingCompaniesPostulations
            onIgnoreBid={onIgnoreBid}
            onRejectBid={onRejectBid}
            onAcceptBid={onAcceptBid}
            onViewService={onViewService}
          />
        </Tabs.TabPane>
      </Tabs>
      <ServiceDetails
        visible={!!serviceToShow}
        onClose={onCloseServiceDetailsDrawer}
        serviceId={serviceToShow}
      />
    </>
  );
};
